import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
  Rating,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Search,
  Inventory,
  LocalOffer,
  Star,
  ArrowBack,
  TrendingUp,
  Add,
  Edit,
  Delete,
  TrendingDown,
  Business,
  Description,
} from "@mui/icons-material";
import { itemsAPI, suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";

export const InvSupplierProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    productId: "",
    costPrice: "",
    minOrderQuantity: 1,
    notes: "",
  });

  useEffect(() => {
    fetchProducts();
    fetchAllProducts();
  }, [id]);

  useEffect(() => {
    const filtered = products.filter(
      (supplierItem) =>
        supplierItem.item?.name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        supplierItem.item?.category
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        supplierItem.item?.brand?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    try {
      const response = await suppliersAPI.getItems(id);
      setProducts(response.data.data.items || []);
    } catch (error) {
      enqueueSnackbar("Error fetching supplier items", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await itemsAPI.getAll();
      setAllProducts(response.data.data.items || []);
    } catch (error) {
      enqueueSnackbar("Error fetching items", { variant: "error" });
    }
  };

  const handleAddProduct = async () => {
    try {
      await suppliersAPI.addItem(id, {
        itemId: formData.productId,
        costPrice: formData.costPrice,
        minOrderQuantity: formData.minOrderQuantity,
        notes: formData.notes
      });
      enqueueSnackbar("Item added to supplier successfully", {
        variant: "success",
      });
      setAddDialog(false);
      setFormData({
        productId: "",
        costPrice: "",
        minOrderQuantity: 1,
        notes: "",
      });
      fetchProducts();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error adding item to supplier",
        { variant: "error" }
      );
    }
  };

  const handleEditProduct = async () => {
    try {
      await suppliersAPI.updateItem(id, selectedProduct._id, {
        itemId: formData.productId,
        costPrice: formData.costPrice,
        minOrderQuantity: formData.minOrderQuantity,
        notes: formData.notes
      });
      enqueueSnackbar("Item updated successfully", { variant: "success" });
      setEditDialog(false);
      setSelectedProduct(null);
      setFormData({
        productId: "",
        costPrice: "",
        minOrderQuantity: 1,
        notes: "",
      });
      fetchProducts();
    } catch (error) {
      enqueueSnackbar("Error updating item", { variant: "error" });
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await suppliersAPI.removeItem(id, productId);
      enqueueSnackbar("Item removed from supplier successfully", {
        variant: "success",
      });
      fetchProducts();
    } catch (error) {
      enqueueSnackbar("Error removing item from supplier", {
        variant: "error",
      });
    }
  };

  const getStockStatus = (stock) => {
    if (stock === 0) return { label: "Out of Stock", color: "error" };
    if (stock < 10) return { label: "Low Stock", color: "warning" };
    return { label: "In Stock", color: "success" };
  };

  const getProfitMarginColor = (margin) => {
    if (margin > 50) return "success";
    if (margin > 20) return "primary";
    if (margin > 0) return "warning";
    return "error";
  };

  const calculateMetrics = () => {
    const activeProducts = products.filter((p) => p.isActive);
    const totalProducts = products.length;
    const totalValue = products.reduce((sum, p) => {
      return sum + (p.costPrice * (p.minOrderQuantity || 0));
    }, 0);
    const avgProfitMargin =
      products.length > 0
        ? products.reduce((acc, p) => acc + (p.profitMargin || 0), 0) /
        products.length
        : 0;

    return {
      totalProducts,
      activeProducts: activeProducts.length,
      totalValue,
      avgProfitMargin,
    };
  };

  const metrics = calculateMetrics();

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Box sx={{ p: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/hospital/hospital-inventory/suppliers")}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Back to Suppliers
            </Button>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                background: `linear(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                fontSize: '2.5rem'
              }}
            >
              Supplier Products
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialog(true)}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700,
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            Add Product
          </Button>
        </Box>

        {/* Enhanced Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 4,
                background: `linear(135deg, ${theme.palette.primary.light}20, ${theme.palette.primary.main}20)`,
                border: `1px solid ${theme.palette.primary.light}30`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 8
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Inventory sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h3" fontWeight="bold" sx={{ fontSize: '2.5rem' }}>
                  {metrics.totalProducts}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mt: 1 }}>
                  Total Products
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 4,
                background: `linear(135deg, ${theme.palette.success.light}20, ${theme.palette.success.main}20)`,
                border: `1px solid ${theme.palette.success.light}30`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 8
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <LocalOffer sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h3" fontWeight="bold" color="success.main" sx={{ fontSize: '2.5rem' }}>
                  {metrics.activeProducts}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mt: 1 }}>
                  Active Products
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 4,
                background: `linear(135deg, ${theme.palette.warning.light}20, ${theme.palette.warning.main}20)`,
                border: `1px solid ${theme.palette.warning.light}30`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 8
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <TrendingDown sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                <Typography variant="h3" fontWeight="bold" color="warning.main" sx={{ fontSize: '2.5rem' }}>
                  {products.filter(p => !p.isActive).length}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mt: 1 }}>
                  Inactive Products
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 4,
                background: `linear(135deg, ${theme.palette.secondary.light}20, ${theme.palette.secondary.main}20)`,
                border: `1px solid ${theme.palette.secondary.light}30`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 8
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Star sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h3" fontWeight="bold" color="secondary.main" sx={{ fontSize: '2.5rem' }}>
                  ₹{(metrics.totalValue / 1000).toFixed(1)}K
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem', mt: 1 }}>
                  Inventory Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Enhanced Search */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: 3,
            background: `linear(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`
          }}
        >
          <CardContent sx={{ py: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search products by name, category, brand, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: '1.1rem',
                  padding: '12px 16px',
                  '& input': {
                    fontSize: '1.1rem',
                    fontWeight: 500
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 28, color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>

        {/* Enhanced Products Table */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 4,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography
                variant="h4"
                gutterBottom
                fontWeight="bold"
                sx={{
                  fontSize: '1.8rem',
                  color: theme.palette.primary.main
                }}
              >
                Product Catalog
              </Typography>
            </Box>

            {filteredProducts.length === 0 ? (
              <Box sx={{ p: 4 }}>
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': {
                      fontSize: '1.1rem'
                    }
                  }}
                >
                  No products found for this supplier.
                </Alert>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                      <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Item Details</TableCell>
                      <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Pricing</TableCell>
                      <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Item Info</TableCell>
                      <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Additional Info</TableCell>
                      <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Status</TableCell>
                      <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, py: 3 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProducts.map((supplierItem) => (
                      <TableRow
                        key={supplierItem._id}
                        hover
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            transform: 'scale(1.002)'
                          }
                        }}
                      >
                        <TableCell sx={{ py: 3 }}>
                          <Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem', mb: 1 }}>
                              {supplierItem.item?.name || "N/A"}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', mb: 2 }}>
                              {supplierItem.item?.brand || "N/A"}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={supplierItem.item?.category || "N/A"}
                                size="medium"
                                variant="outlined"
                                sx={{ fontSize: '0.9rem', fontWeight: 600 }}
                              />
                              <Chip
                                label={supplierItem.item?.itemType || "N/A"}
                                size="medium"
                                variant="filled"
                                color="primary"
                                sx={{ fontSize: '0.9rem', fontWeight: 600 }}
                              />
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 3 }}>
                          <Box>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              Cost Price
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.3rem', color: theme.palette.success.main }}>
                              ₹{supplierItem.costPrice?.toLocaleString()}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontSize: '1rem' }}>
                              Min Order Qty
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {supplierItem.minOrderQuantity || 1} {supplierItem.item?.unit || ""}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 3 }}>
                          <Box>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              Item Type
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                              {supplierItem.item?.itemType || "N/A"}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 2, fontSize: '1rem' }} color="text.secondary">
                              Unit
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {supplierItem.item?.unit || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 3 }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Business sx={{ fontSize: 20, color: 'text.secondary' }} />
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                Manufacturer
                              </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2 }}>
                              {supplierItem.item?.manufacturer || "N/A"}
                            </Typography>
                            {supplierItem.notes && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Description sx={{ fontSize: 18, color: 'text.secondary', mt: 0.5 }} />
                                <Typography variant="body2" sx={{ fontSize: '0.95rem' }} color="text.secondary">
                                  <strong>Note:</strong> {supplierItem.notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 3 }}>
                          <Box>
                            <Chip
                              label={supplierItem.isActive ? "Active" : "Inactive"}
                              size="medium"
                              color={supplierItem.isActive ? "success" : "default"}
                              sx={{
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                px: 1
                              }}
                            />
                            {supplierItem.lastOrderDate && (
                              <Typography
                                variant="body2"
                                sx={{ mt: 2, fontSize: '0.9rem' }}
                                color="text.secondary"
                              >
                                Last Order:{" "}
                                {new Date(
                                  supplierItem.lastOrderDate
                                ).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flexDirection: "column",
                              alignItems: 'flex-start'
                            }}
                          >
                            <Tooltip title="Edit Item">
                              <IconButton
                                size="large"
                                onClick={() => {
                                  setSelectedProduct(supplierItem);
                                  setFormData({
                                    productId: supplierItem.item?._id,
                                    costPrice: supplierItem.costPrice,
                                    minOrderQuantity: supplierItem.minOrderQuantity,
                                    notes: supplierItem.notes || "",
                                  });
                                  setEditDialog(true);
                                }}
                                color="primary"
                                sx={{
                                  backgroundColor: theme.palette.primary.light + '20',
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.light + '40',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Edit sx={{ fontSize: 22 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Remove Item">
                              <IconButton
                                size="large"
                                onClick={() => handleDeleteProduct(supplierItem._id)}
                                color="error"
                                sx={{
                                  backgroundColor: theme.palette.error.light + '20',
                                  '&:hover': {
                                    backgroundColor: theme.palette.error.light + '40',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Delete sx={{ fontSize: 22 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Enhanced Add Product Dialog */}
        <Dialog
          open={addDialog}
          onClose={() => setAddDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: 24
            }
          }}
        >
          <DialogTitle
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              py: 3
            }}
          >
            Add Item to Supplier
          </DialogTitle>
          <DialogContent sx={{ py: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Select Item</InputLabel>
                  <Select
                    value={formData.productId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        productId: e.target.value,
                      }))
                    }
                    label="Select Item"
                    sx={{
                      fontSize: '1.1rem',
                      '& .MuiSelect-select': {
                        py: 2
                      }
                    }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: '1.1rem' }}>
                      Select Item
                    </MenuItem>
                    {allProducts.map((item) => (
                      <MenuItem key={item._id} value={item._id} sx={{ fontSize: '1.1rem' }}>
                        {item.name} - {item.brand} ({item.itemType})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cost Price"
                  value={formData.costPrice}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      costPrice: parseFloat(e.target.value) || 0,
                    }))
                  }
                  required
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem', py: 2 }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>₹</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Order Quantity"
                  value={formData.minOrderQuantity}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      minOrderQuantity: parseInt(e.target.value) || 1,
                    }))
                  }
                  inputProps={{ min: 1 }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem', py: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Supplier Notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Any specific notes about this supplier for this item..."
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 3 }}>
            <Button
              onClick={() => setAddDialog(false)}
              variant="outlined"
              sx={{
                fontSize: '1.1rem',
                px: 4,
                py: 1,
                borderRadius: 2
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddProduct}
              variant="contained"
              disabled={!formData.productId || !formData.costPrice}
              sx={{
                fontSize: '1.1rem',
                px: 4,
                py: 1,
                borderRadius: 2,
                fontWeight: 700
              }}
            >
              Add Item
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Edit Product Dialog */}
        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: 24
            }
          }}
        >
          <DialogTitle
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              backgroundColor: theme.palette.info.main,
              color: 'white',
              py: 3
            }}
          >
            Edit Item Details
          </DialogTitle>
          <DialogContent sx={{ py: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': {
                      fontSize: '1.1rem',
                      fontWeight: 600
                    }
                  }}
                >
                  Editing: {selectedProduct?.item?.name}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cost Price"
                  value={formData.costPrice}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      costPrice: parseFloat(e.target.value) || 0,
                    }))
                  }
                  required
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem', py: 2 }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>₹</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Order Quantity"
                  value={formData.minOrderQuantity}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      minOrderQuantity: parseInt(e.target.value) || 1,
                    }))
                  }
                  inputProps={{ min: 1 }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem', py: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Supplier Notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 3 }}>
            <Button
              onClick={() => setEditDialog(false)}
              variant="outlined"
              sx={{
                fontSize: '1.1rem',
                px: 4,
                py: 1,
                borderRadius: 2
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditProduct}
              variant="contained"
              sx={{
                fontSize: '1.1rem',
                px: 4,
                py: 1,
                borderRadius: 2,
                fontWeight: 700
              }}
            >
              Update Item
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};